import React, { useRef } from 'react';
import { Product, PreviewChannel, WorkspaceView } from '../types';
import { formatMoney, getAmazonProductUrl } from '../utils';

interface PreviewViewProps {
  active: boolean;
  products: Product[];
  selectedProduct: Product | null;
  previewChannel: PreviewChannel;
  sellerId: string;
  onSelectProduct: (productId: string) => void;
  onChangePreviewChannel: (channel: PreviewChannel) => void;
  onSimulateOrder: (product: Product, channelLabel: string, triggerEl: HTMLElement | null) => void;
  onSwitchView: (view: WorkspaceView) => void;
}

const CHANNELS: { key: PreviewChannel; label: string }[] = [
  { key: 'amazon', label: 'Amazon Prime' },
  { key: 'shopify', label: 'Shopify Store' },
  { key: 'tiktok', label: 'TikTok Shop' },
  { key: 'walmart', label: 'Walmart' }
];

export const PreviewView: React.FC<PreviewViewProps> = ({
  active,
  products,
  selectedProduct,
  previewChannel,
  sellerId,
  onSelectProduct,
  onChangePreviewChannel,
  onSimulateOrder,
  onSwitchView
}) => {
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const simulateBtnRef = useRef<HTMLButtonElement>(null);

  const p = selectedProduct || products[0];
  if (!p) {
    return <div id="view-preview" className="workspace-view" role="tabpanel" aria-labelledby="tab-preview" hidden={!active} />;
  }

  const channelMap: Record<
    PreviewChannel,
    { name: string; url: string; referralRate: number; fulfillmentFee: number; cta: string; badge: string }
  > = {
    amazon: {
      name: 'Amazon.com — Prime 1-Day Delivery',
      url: getAmazonProductUrl(p, sellerId),
      referralRate: 0.15,
      fulfillmentFee: 4.75,
      cta: 'Buy Now with Prime 1-Day',
      badge: 'PRIME FREE ONE-DAY DELIVERY'
    },
    shopify: {
      name: 'Vela Studio Official Store (Shopify DTC)',
      url: `https://velastudio.shop/products/${p.id}`,
      referralRate: 0.029,
      fulfillmentFee: 5.4,
      cta: 'Add to Bag — Free Express Shipping',
      badge: 'OFFICIAL BRAND STOREFRONT · 2.9% FEE'
    },
    tiktok: {
      name: 'TikTok Shop US — Fulfilled by TikTok',
      url: `https://shop.tiktok.com/@velastudio/product/${p.id}`,
      referralRate: 0.06,
      fulfillmentFee: 3.9,
      cta: 'Buy Now on TikTok Shop',
      badge: 'TIKTOK VIRAL DEAL · FREE SHIPPING'
    },
    walmart: {
      name: 'Walmart.com — Fulfilled by Walmart (WFS)',
      url: `https://www.walmart.com/ip/${p.id}`,
      referralRate: 0.15,
      fulfillmentFee: 4.25,
      cta: 'Add to Cart — Walmart+ 2-Day Shipping',
      badge: 'WALMART FULFILLMENT SERVICES'
    }
  };

  const ch = channelMap[previewChannel] || channelMap.amazon;
  const landed = +(p.pricing.alibabaFobUnit + p.pricing.ddpShippingUnit).toFixed(2);
  const fees = +(p.pricing.amazonRetailPrice * ch.referralRate + ch.fulfillmentFee).toFixed(2);
  const net = +(p.pricing.amazonRetailPrice - landed - fees - p.pricing.ppcEstUnit).toFixed(2);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = CHANNELS.findIndex((c) => c.key === previewChannel);
    if (currentIndex === -1) return;

    let nextIndex = -1;
    if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % CHANNELS.length;
    else if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + CHANNELS.length) % CHANNELS.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = CHANNELS.length - 1;

    if (nextIndex !== -1) {
      e.preventDefault();
      onChangePreviewChannel(CHANNELS[nextIndex].key);
      pillRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div id="view-preview" className={`workspace-view ${active ? 'active' : ''}`} role="tabpanel" aria-labelledby="tab-preview" hidden={!active}>
      <section className="view-section">
        <div className="container">
          <div className="section-bar">
            <div>
              <span className="section-kicker">LIVE CUSTOMER EXPERIENCE</span>
              <h2>Customer Storefront Preview</h2>
            </div>

            <div className="preview-controls">
              <label htmlFor="preview-product-select" className="sr-only">
                Product to Preview
              </label>
              <select
                id="preview-product-select"
                className="input-select tabular"
                value={p.id}
                onChange={(e) => onSelectProduct(e.target.value)}
              >
                {products.map((prod) => (
                  <option key={prod.id} value={prod.id}>
                    #{prod.rank} · {prod.title}
                  </option>
                ))}
              </select>

              <div className="channel-pills" role="tablist" aria-label="Storefront Channel" onKeyDown={handleKeyDown}>
                {CHANNELS.map((c, idx) => {
                  const isActive = previewChannel === c.key;
                  return (
                    <button
                      key={c.key}
                      ref={(el) => {
                        pillRefs.current[idx] = el;
                      }}
                      type="button"
                      className={`channel-pill ${isActive ? 'active' : ''}`}
                      role="tab"
                      aria-selected={isActive ? 'true' : 'false'}
                      tabIndex={isActive ? 0 : -1}
                      data-preview-channel={c.key}
                      onClick={() => onChangePreviewChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>

              <button
                ref={simulateBtnRef}
                type="button"
                id="simulate-order-btn"
                className="btn-jet"
                onClick={(e) => onSimulateOrder(p, `${previewChannel.toUpperCase()} Storefront Checkout`, e.currentTarget)}
              >
                Simulate Customer Order (+1 Sale)
              </button>
            </div>
          </div>

          <div className="browser-frame">
            <div className="browser-bar">
              <div className="browser-dots" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <a
                href={ch.url}
                target="_blank"
                rel="noopener noreferrer"
                className="browser-url tabular truncate"
                id="preview-url-bar"
                title="Open live channel URL in new tab"
              >
                {ch.url}
              </a>
              <div className="browser-profit-tag tabular" id="preview-profit-pill">
                Your Net Profit: +{formatMoney(net)} / sale
              </div>
            </div>

            <div id="storefront-stage" className="storefront-stage" aria-live="polite">
              <div className="storefront-mockup-grid">
                <div className="storefront-img-box">
                  <img src={p.images[0]} alt={p.title} />
                </div>
                <div className="storefront-details">
                  <span className="section-kicker">{ch.badge}</span>
                  <h3>{p.amazonListing?.title || p.title}</h3>
                  <div className="storefront-price-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className="storefront-big-price tabular">{formatMoney(p.pricing.amazonRetailPrice)}</span>
                    {(() => {
                      const saleVal = Number(p.pricing.amazonRetailPrice || 39.99);
                      const listVal =
                        p.pricing.listPrice && p.pricing.listPrice > saleVal
                          ? Number(p.pricing.listPrice)
                          : +(Math.ceil(saleVal * 1.28) - 0.01).toFixed(2);
                      const pctVal = p.pricing.discountPct || Math.round(((listVal - saleVal) / listVal) * 100);
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span
                            className="tabular"
                            style={{
                              fontSize: '1.05rem',
                              color: '#71717A',
                              textDecoration: 'line-through',
                              fontWeight: 600
                            }}
                          >
                            {formatMoney(listVal)}
                          </span>
                          <span
                            className="tabular"
                            style={{
                              background: '#DC2626',
                              color: '#FFFFFF',
                              fontSize: '0.76rem',
                              fontWeight: 800,
                              padding: '3px 8px',
                              borderRadius: 6,
                              letterSpacing: '0.03em'
                            }}
                          >
                            -{pctVal}% SALE
                          </span>
                        </span>
                      );
                    })()}
                    <span className="storefront-stock-pill">
                      <span aria-hidden="true">✓</span> In Stock · Ships Automatically from Warehouse
                    </span>
                  </div>
                  <ul className="storefront-bullets">
                    {(p.amazonListing?.bullets || []).slice(0, 4).map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                  <div className="action-row-clean">
                    <button
                      type="button"
                      className="btn-jet"
                      id="stage-buy-btn"
                      onClick={() => onSimulateOrder(p, `${previewChannel.toUpperCase()} Storefront Checkout`, simulateBtnRef.current)}
                    >
                      {ch.cta}
                    </button>
                    <button type="button" className="btn-outline" id="stage-configure-btn" onClick={() => onSwitchView('launch')}>
                      Adjust Pricing &amp; Margin <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
